/**
 * The whole reactivity system. ~40 lines, no dependency.
 *
 * The shell needs exactly one thing from a framework: "when this value changes,
 * re-run that". It does not need a virtual DOM — the protocol already gives
 * every node a stable key, so the tree is keyed at the source and diffing it
 * again through a VDOM would be diffing twice.
 */

type Runner = { run: () => void; cleanups: Set<() => void> };

let active: Runner | null = null;
let depth = 0;
const pending = new Set<() => void>();

export type Signal<T> = {
  get(): T;
  /** Read without subscribing. */
  peek(): T;
  set(next: T): void;
};

export function signal<T>(initial: T, equals: (a: T, b: T) => boolean = Object.is): Signal<T> {
  let value = initial;
  const subscribers = new Set<() => void>();

  return {
    get(): T {
      const runner = active;
      if (runner !== null) {
        subscribers.add(runner.run);
        runner.cleanups.add(() => subscribers.delete(runner.run));
      }
      return value;
    },
    peek: () => value,
    set(next: T): void {
      if (equals(value, next)) return;
      value = next;
      for (const subscriber of [...subscribers]) {
        if (depth > 0) pending.add(subscriber);
        else subscriber();
      }
    },
  };
}

/** Run `fn` now, and again whenever a signal it read changes. */
export function effect(fn: () => void): () => void {
  let disposed = false;
  const runner: Runner = { run: () => undefined, cleanups: new Set() };

  runner.run = (): void => {
    if (disposed) return;
    for (const cleanup of runner.cleanups) cleanup();
    runner.cleanups.clear();
    const previous = active;
    active = runner;
    try {
      fn();
    } finally {
      active = previous;
    }
  };

  runner.run();
  return () => {
    disposed = true;
    for (const cleanup of runner.cleanups) cleanup();
    runner.cleanups.clear();
  };
}

/** Coalesce the effects of several writes into one pass. */
export function batch(fn: () => void): void {
  depth++;
  try {
    fn();
  } finally {
    depth--;
    if (depth === 0) {
      const queued = [...pending];
      pending.clear();
      for (const subscriber of queued) subscriber();
    }
  }
}
